import React, { useState } from "react";
import { motion } from "framer-motion";
import { FaNetworkWired, FaServer, FaHeadset, FaCalculator } from "react-icons/fa";
import "./SipTrunking.css";

export default function SipTrunking(): JSX.Element {
  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: "easeOut" },
    },
  };

  const sipPlans = [
    {
      name: "PAYG",
      price: "$20.00 /channel/mo (inc. GST)",
      cisLabel: "Critical Information Summary",
      cisHref:
        "/docs/Jortel%20Business%20Line%20PAYG%20Critical%20Information%20Summary.pdf",
      features: [
        "No DIDs included",
        "Scalable trunks — 1 to 1000 channels",
        "Instant provisioning",
      ],
    },
    {
      name: "Premium",
      price: "$45.00 /channel/mo (inc. GST)",
      cisLabel: "Critical Information Summary",
      cisHref:
        "/docs/Jortel%20Business%20Line%20Value%20Critical%20Information%20Summary.pdf",
      features: [
        "No DIDs included",
        "Voicemail",
        "Comportal",
        "Call Manager",
        "Scalable trunks — 1 to 1000 channels",
        "Instant provisioning",
      ],
      popular: true,
    },
  ];

  const callRates = {
    premium: [
      { type: "Local", rate: "Unlimited" },
      { type: "National", rate: "Unlimited" },
      { type: "Mobile", rate: "Unlimited" },
      { type: "13/1300 Numbers", rate: "$0.45/call" },
      { type: "1800 Numbers", rate: "Unlimited" },
    ],
    payg: [
      { type: "Local", rate: "$0.22/min" },
      { type: "National", rate: "$0.22/min" },
      { type: "Mobile", rate: "$0.45/min" },
      { type: "13/1300 Numbers", rate: "$0.45/call" },
      { type: "1800 Numbers", rate: "Unlimited" },
    ],
  };

  const didBlocks = [
    { pack: "1 GEO DID", price: "$5.00" },
    { pack: "10 GEO DIDs", price: "$25.00" },
    { pack: "100 GEO DIDs", price: "$50.00" },
  ];

  const notes = [
    "All pricing includes GST.",
    "Services are billed monthly per channel.",
    "Call charges are rounded to the nearest cent.",
    "Timed calls billed per second increments.",
    "Terms and conditions apply. Contact 1300 567 835 for details.",
  ];

  const [channels, setChannels] = useState(10);

  return (
    <motion.main
      className="page sip-page"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      {/* HERO */}
      <section className="sip-hero">
        <h1>J-Voice SIP (Small Office)</h1>
        <p>
          Flexible business-grade SIP trunking for modern PBX systems. Scale
          from a single line to thousands of channels with instant provisioning
          and transparent pricing.
        </p>
      </section>

      {/* CHANNEL CALCULATOR */}
      <section className="channel-calc">
        <motion.div
          className="channel-calc__card"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="channel-calc__header">
            <div className="channel-calc__icon">
              <FaCalculator />
            </div>
            <span>Channel Calculator</span>
          </div>

          <div className="channel-calc__slider">
            <div className="channel-calc__range-labels">
              <span>1 Channel</span>
              <span>100 Channels</span>
            </div>
            <div className="channel-calc__bar">
              <div
                className="channel-calc__progress"
                style={{ width: `${(channels / 100) * 100}%` }}
              />
              <input
                type="range"
                min="1"
                max="100"
                value={channels}
                onChange={(e) => setChannels(parseInt(e.target.value, 10))}
                aria-label="Select number of SIP channels"
              />
            </div>
            <div className="channel-calc__value">
              <span className="channel-calc__value-main">{channels}</span>
              <span className="channel-calc__value-sub">channels</span>
            </div>
          </div>

          <div className="channel-calc__totals">
            <div className="channel-calc__total-card highlight">
              <div className="label">Premium Monthly</div>
              <div className="value">${(channels * 45).toFixed(2)}</div>
            </div>
            <div className="channel-calc__total-card">
              <div className="label">PAYG Monthly</div>
              <div className="value">${(channels * 20).toFixed(2)}</div>
            </div>
          </div>
        </motion.div>
      </section>

      <div className="sip-flex-row">
        {/* PLAN CARDS */}
        <motion.div
          className="sip-grid"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {sipPlans.map((plan, i) => (
            <motion.div
              key={i}
              variants={cardVariants}
              whileHover={{ scale: 1.04 }}
              className={`sip-card ${plan.popular ? "popular" : ""}`}
            >
              {plan.popular && (
                <div className="ribbon">
                  <span className="shimmer">Most Popular</span>
                </div>
              )}
              <h3>{plan.name}</h3>
              <div className="price">{plan.price}</div>
              <p className="gst-note">(Includes GST)</p>

              <h4>Key Features</h4>
              <ul className="feature-list">
                {plan.features.map((f, idx) => (
                  <li key={idx}>{f}</li>
                ))}
              </ul>
              <a
                className="sip-cis"
                href={plan.cisHref}
                target="_blank"
                rel="noopener noreferrer"
              >
                {plan.cisLabel}
              </a>
            </motion.div>
          ))}
        </motion.div>

        {/* CALL RATES / DIDs / NOTES (Accordion) */}
        <div className="sip-dropdowns">
          <section className="sip-accordion">
            <div className="sip-accordion-item open">
              <div
                className="sip-accordion-header"
                onClick={(e) => e.currentTarget.parentElement?.classList.toggle("open")}
              >
                <span>Call Rates</span>
                <span className="chevron">▾</span>
              </div>
              <div className="sip-accordion-body">
                <div className="sip-table-grid">
                  <div className="sip-table-wrapper">
                    <h4>Premium &amp; Premium Access</h4>
                    <table>
                      <thead>
                        <tr>
                          <th>Call Type</th>
                          <th>Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {callRates.premium.map((r, i) => (
                          <tr key={i}>
                            <td>{r.type}</td>
                            <td>{r.rate}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="sip-table-wrapper">
                    <h4>PAYG Plan</h4>
                    <table>
                      <thead>
                        <tr>
                          <th>Call Type</th>
                          <th>Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {callRates.payg.map((r, i) => (
                          <tr key={i}>
                            <td>{r.type}</td>
                            <td>{r.rate}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <div className="sip-accordion-item">
              <div
                className="sip-accordion-header"
                onClick={(e) => e.currentTarget.parentElement?.classList.toggle("open")}
              >
                <span>Direct In-Dial (DID) Blocks</span>
                <span className="chevron">▾</span>
              </div>
              <div className="sip-accordion-body">
                <div className="sip-table-wrapper full-width">
                  <table>
                    <thead>
                      <tr>
                        <th>DID Pack</th>
                        <th>Price (inc. GST)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {didBlocks.map((d, i) => (
                        <tr key={i}>
                          <td>{d.pack}</td>
                          <td>{d.price}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="sip-accordion-item">
              <div
                className="sip-accordion-header"
                onClick={(e) => e.currentTarget.parentElement?.classList.toggle("open")}
              >
                <span>Notes</span>
                <span className="chevron">▾</span>
              </div>
              <div className="sip-accordion-body">
                <div className="sip-box">
                  <ul>
                    {notes.map((n, i) => (
                      <li key={i}>{n}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* WHY CHOOSE JORTEL */}
      <motion.section
        className="advantages-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={cardVariants}
      >
        <h2>Why Businesses Choose Jortel SIP</h2>
        <div className="advantages-grid">
          {[ 
            {
              icon: <FaNetworkWired className="adv-icon" />,
              title: "Carrier-Grade Network",
              text: "Our SIP infrastructure is optimised for uptime and call clarity across Australia.",
            },
            {
              icon: <FaServer className="adv-icon" />,
              title: "PBX Integration",
              text: "Compatible with major PBX systems including Cisco, 3CX and Yealink.",
            },
            {
              icon: <FaHeadset className="adv-icon" />,
              title: "24/7 Support",
              text: "Get help anytime with dedicated business support — no offshore queues.",
            },
          ].map((adv, i) => (
            <motion.div
              key={i}
              variants={cardVariants}
              whileHover={{ scale: 1.05 }}
              className="adv-card"
            >
              {adv.icon}
              <h3>{adv.title}</h3>
              <p>{adv.text}</p>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* CTA PANEL */}
      <motion.section
        className="advisor-panel"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={cardVariants}
      >
        <div className="advisor-content">
          <h2>Upgrade Your Business Voice</h2>
          <p>
            Our team can set up SIP trunks tailored for your PBX or hosted
            environment — get connected in minutes.
          </p>
          <a href="/contact" className="btn primary">
            Get Started Today
          </a>
        </div>
      </motion.section>
    </motion.main>
  );
}

